import React, { useEffect, useRef } from 'react'
import { Button, Form, Typography } from 'antd'

import IntlMessages from '../../../../util/IntlMessages'
import SetPassword from '../../../../components/set-password'
import CodeField, { ICodeFieldHandle } from './code-field'

const { Paragraph, Link } = Typography

export interface IResetCodeStepProps {
  isBusy: boolean
  onSubmit: (params: { code: string; password: string }) => void
  onCodeChanged: () => void
  onResendCode: () => void
  onBackToSignin: () => void
}

const ResetCodeStep = ({ isBusy, onSubmit, onCodeChanged, onResendCode, onBackToSignin }: IResetCodeStepProps): React.ReactElement => {
  const [form] = Form.useForm()
  const codeRef = useRef<ICodeFieldHandle>(null)
  const wasBusy = useRef(false)
  const resendPending = useRef(false)

  useEffect(() => {
    codeRef.current?.focus()
  }, [])

  // The boxes are disabled while the resend runs, so the caret can only go back into them
  // once it is over.
  useEffect(() => {
    if (wasBusy.current && !isBusy && resendPending.current) {
      resendPending.current = false
      codeRef.current?.focus()
    }
    wasBusy.current = isBusy
  }, [isBusy])

  // The code on screen stops working the moment a new one is issued, so it goes immediately
  // rather than waiting for the visitor to notice it is stale. Only one request at a time:
  // each new code invalidates the last, so overlapping ones leave no code that works.
  const resendCode = () => {
    if (isBusy) return
    form.resetFields(['code'])
    resendPending.current = true
    onResendCode()
  }

  return (
    <>
      <Paragraph className="connect-with-email">
        <IntlMessages id="app.code" />
      </Paragraph>
      <Form name="connect-with-email" form={form} layout="vertical" onFinish={(values) => onSubmit({ code: values.code, password: values.password })}>
        <CodeField
          ref={codeRef}
          disabled={isBusy}
          requiredMessageId="signin.verificationCodeRequired"
          dataQa="reset-code"
          className="reset-code-item"
          onChanged={onCodeChanged}
          // Typed or pasted, a finished code means the next thing to fill is the password.
          // Leaving the last box also clears the digit antd selects inside it.
          onComplete={() => form.getFieldInstance('password')?.focus()}
        />
        <SetPassword />
        <Form.Item>
          <Button size="large" type="primary" htmlType="submit" disabled={isBusy} loading={isBusy} data-qa="reset-submit" block>
            <IntlMessages id="app.resetPassword" />
          </Button>
        </Form.Item>
      </Form>
      <Paragraph className="signin-other-options">
        {/* antd only greys a disabled link out, so assistive tech is told separately. */}
        <Link onClick={resendCode} disabled={isBusy} aria-disabled={isBusy} data-qa="reset-resend-code">
          <IntlMessages id="app.resendCode" />
        </Link>
      </Paragraph>
      <Paragraph className="auth-sidebar-login">
        <Link onClick={onBackToSignin} data-qa="reset-back-to-signin">
          <IntlMessages id="signin.backToSignin" />
        </Link>
      </Paragraph>
    </>
  )
}

export default ResetCodeStep
